import {Plural} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {DateCell} from '../../components/common/table/cells/dateCell';
import {NameCell} from '../../components/common/table/cells/nameCell';
import {TableCellProps} from '../../components/common/table/cells/tableCell';
import {TextCell} from '../../components/common/table/cells/textCell';
import {TableRow, TableRowRenderOptions} from '../../components/common/table/tableRow';
import {CircleModel} from '../../models/api/circleModel';
import {SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {useCircleNavigateToLink} from './circlesHelpers';
import {circlesTableColumns, CirclesTableColumnsEnum} from './circlesTableConfig';

/*
 * Types.
 */

interface CircleTableCellProps extends TableCellProps<number> {
  shouldRemoveTopBorderRadius: boolean;
  shouldRemoveBottomBorderRadius: boolean;
  isInMultiSelectMode: boolean;
}

type CellRender = (circle: CircleModel, cellProps: CircleTableCellProps) => ReactNode;

/*
 * Styles.
 */

const StyledTableRow = styled(TableRow)<{$minHeight: number}>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Props.
 */

interface CircleTableRowProps {
  circle: CircleModel;
  options: TableRowRenderOptions;
  isSelected: boolean;
  onSelect: Handler;
  shouldRemoveBottomBorderRadius: boolean;
  shouldRemoveTopBorderRadius: boolean;
  isInMultiSelectMode: boolean;
}

/*
 * Component.
 */

export const CircleTableRow: FC<CircleTableRowProps> = (props) => {
  const {
    circle,
    options,
    isInMultiSelectMode,
    isSelected,
    onSelect,
    shouldRemoveBottomBorderRadius,
    shouldRemoveTopBorderRadius,
  } = props;

  const navigateTo = useCircleNavigateToLink();

  return (
    <StyledTableRow
      $minHeight={options.tableRowHeight}
      hasHover
      renderColumn={(isHovering) =>
        circlesTableColumns.map((column, index) => (
          <React.Fragment key={column.id}>
            {columnRender[column.type](circle, {
              ...options,
              index,
              isHovering,
              isSelected,
              settings: column.settings,
              isInMultiSelectMode,
              shouldRemoveTopBorderRadius,
              shouldRemoveBottomBorderRadius,
            })}
          </React.Fragment>
        ))
      }
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      shouldShowRightGradient={options.shouldShowRightGradient}
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        if (isInMultiSelectMode) {
          onSelect();
          return;
        }

        navigateTo(SettingsResourcePagesEnum.EDIT, {circleId: circle.id});
      }}
      onCheckBoxClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        event.preventDefault();
        onSelect();
      }}
      isSelected={isSelected}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};

/*
 * Helpers.
 */

const columnRender: Record<CirclesTableColumnsEnum, CellRender> = {
  [CirclesTableColumnsEnum.NAME]: (circle, cellProps) => (
    <NameCell {...cellProps} value={circle.name} description={circle.description} iconName="group" />
  ),
  [CirclesTableColumnsEnum.TEAMMATES]: (circle, cellProps) => (
    <TextCell
      {...cellProps}
      value={circle.teammateIds.length.toString()}
      renderValue={() => (
        <Plural
          id="circles-table-row-members"
          value={circle.teammateIds.length}
          one="# member"
          other="# members"
        />
      )}
    />
  ),
  [CirclesTableColumnsEnum.CREATED_AT]: (circle, cellProps) => (
    <DateCell
      {...cellProps}
      value={circle.createdAt}
      customFormat={{day: '2-digit', month: 'short', year: 'numeric'}}
    />
  ),
};
